import { computed, ref, shallowRef } from 'vue'
import type {
  Notification,
  NotificationListResponse,
} from '@/utils/types/notification'
import {
  notificationList as notificationListRequest,
  notificationMarkAllAsRead,
  notificationMarkAsRead,
  notificationUnreadCount,
} from '@/requests/notifications'

export type NotificationTab = 'all' | 'unread' | 'documents' | 'system'
type NotificationAppearance = {
  avatarColor: string
  icon: string
  iconColor: string
}

const notificationList = shallowRef<Notification[]>([])
const unreadCount = ref<number>(0)
const activeTab = ref<NotificationTab>('all')

const tabs: { value: NotificationTab; label: string }[] = [
  { value: 'all', label: 'Всі' },
  { value: 'unread', label: 'Непрочитані' },
  { value: 'documents', label: 'Документи' },
  { value: 'system', label: 'Система' },
]

export function useNotificationList() {
  const isUnread = (item: Notification): boolean => {
    return item.statusId !== 2
  }

  const getEntityTypeName = (item: Notification): string => {
    return item.notification.entityType.name.toLowerCase()
  }

  const isDocumentNotification = (item: Notification): boolean => {
    const entityTypeName = getEntityTypeName(item)
    return (
      entityTypeName.includes('документ') || entityTypeName.includes('document')
    )
  }

  const isSystemNotification = (item: Notification): boolean => {
    const entityTypeName = getEntityTypeName(item)
    return (
      entityTypeName.includes('система') || entityTypeName.includes('system')
    )
  }

  const filteredNotificationList = computed(() => {
    if (activeTab.value === 'unread') {
      return notificationList.value.filter(isUnread)
    }

    if (activeTab.value === 'documents') {
      return notificationList.value.filter(isDocumentNotification)
    }

    if (activeTab.value === 'system') {
      return notificationList.value.filter(isSystemNotification)
    }

    return notificationList.value
  })

  const getNotificationAppearance = (
    item: Notification
  ): NotificationAppearance => {
    const normalizedText = [
      item.notification.entityType.name,
      item.notification.message,
      item.status.name,
    ]
      .join(' ')
      .toLowerCase()

    if (
      item.statusId === 2 ||
      normalizedText.includes('завершено') ||
      normalizedText.includes('підписано') ||
      normalizedText.includes('погоджено') ||
      normalizedText.includes('успішно')
    ) {
      return {
        avatarColor: 'green-lighten-5',
        icon: 'mdi-check',
        iconColor: 'success',
      }
    }

    if (
      normalizedText.includes('система') ||
      normalizedText.includes('резерв') ||
      normalizedText.includes('backup') ||
      normalizedText.includes('оновлено') ||
      normalizedText.includes('reload')
    ) {
      return {
        avatarColor: 'red-lighten-5',
        icon: 'mdi-reload-alert',
        iconColor: 'error',
      }
    }

    return {
      avatarColor: 'blue-lighten-5',
      icon: 'mdi-alert-outline',
      iconColor: 'primary',
    }
  }

  const getNotificationUnreadCount = async (): Promise<number> => {
    const response = await notificationUnreadCount()
    unreadCount.value = response.count

    return unreadCount.value
  }

  const notificationRead = async (notificationId: number): Promise<void> => {
    const notification = notificationList.value.find(
      item => item.id === notificationId
    )

    if (!notification || !isUnread(notification)) {
      return
    }

    await notificationMarkAsRead(notificationId)

    notificationList.value = notificationList.value.map(item =>
      item.id === notificationId
        ? {
            ...item,
            statusId: 2,
            status: {
              ...item.status,
              id: 2,
            },
          }
        : item
    )

    unreadCount.value = Math.max(unreadCount.value - 1, 0)
  }

  const notificationReadAll = async (): Promise<void> => {
    await notificationMarkAllAsRead()

    notificationList.value = notificationList.value.map(item => ({
      ...item,
      statusId: 2,
      status: {
        ...item.status,
        id: 2,
      },
    }))
    unreadCount.value = 0
  }

  const addIncomingNotification = (item: Notification): void => {
    if (notificationList.value.some(n => n.id === item.id)) {
      return
    }

    notificationList.value = [
      generateNotification(item),
      ...notificationList.value,
    ]

    if (isUnread(item)) {
      unreadCount.value += 1
    }
  }

  const getNotificationList = async (
    page = 1
  ): Promise<NotificationListResponse> => {
    const response = await notificationListRequest({ page })
    notificationList.value = response.data.map(notification =>
      generateNotification(notification)
    )

    return response
  }

  const navigateToNotification = async (item: Notification): Promise<void> => {
    await notificationRead(item.id)

    if (!item.notification.link) {
      return
    }

    window.open(item.notification.link, '_blank')
  }

  return {
    activeTab,
    filteredNotificationList,
    notificationList,
    tabs,
    unreadCount,
    addIncomingNotification,
    getNotificationAppearance,
    getNotificationList,
    getNotificationUnreadCount,
    navigateToNotification,
    notificationRead,
    notificationReadAll,
  }
}

function generateNotification(notification: Notification): Notification {
  return {
    ...notification,
    notification: {
      ...notification.notification,
      message: notification.notification.message,
    },
  }
}
